import { useMutation } from '@tanstack/react-query'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import Select from 'components/Common/Form/Input/Select'
import TextInput from 'components/Common/Form/Input/TextInput'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextButton from 'LuxKit/Button/TextButton'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { formToObject } from 'lib/forms/formToObject'
import { rem } from 'polished'
import type { FormEventHandler } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import type { ExpressionOfInterestData } from 'agentHub/api/requestPostExpressionOfInterest'
import requestPostExpressionOfInterest from 'agentHub/api/requestPostExpressionOfInterest'
import clsx from 'clsx'
import TextLink from 'LuxKit/TextLink'
import { connect } from 'react-redux'
import AgentHubAffiliationAutocomplete from 'agentHub/components/AgentHubAffiliationAutocomplete'
import { AGENT_HUB_DEFAULT_PHONE_NUMBER_PREFIX } from 'constants/config/region'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchAgentAffiliationOptions } from 'actions/AgentHubActions'

import config from 'constants/config'
import { isAgentHubAffiliationRequired } from 'agentHub/selectors/agentHubSelectors'

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:"agencyName agencyName"
                        "regionCode regionCode"
                        "affiliation affiliation"
                        "firstName lastName"
                        "phone email";
    &.showSpecifyAffiliation {
      grid-template-areas:"agencyName agencyName"
                        "regionCode regionCode"
                        "affiliation affiliation"
                        "specifyAffiliation specifyAffiliation"
                        "firstName lastName"
                        "phone email";
    }
    .grid-agencyName {
      grid-area: agencyName;
    }
    .grid-regionCode {
      grid-area: regionCode;
    }
    .grid-firstName {
      grid-area: firstName;
    }
    .grid-lastName{
      grid-area: lastName;
    }
    .grid-phone {
      grid-area: phone;
    }
    .grid-email {
      grid-area: email;
    }
    .grid-affiliation {
      grid-area: affiliation;
    }
    .grid-specifyAffiliation {
      grid-area: specifyAffiliation;
    }
  }
`

export interface FormData {
  agencyName: string
  firstName: string
  lastName: string
  email: string
  affiliationId: string
  phone: { phone: string, prefix: string }
  specifyAffiliation?: string
  regionCode: App.AgentHubEnabledRegions
}

function mapFormData(formData: FormData) {
  return {
    agencyName: formData.agencyName,
    givenName: formData.firstName,
    surname: formData.lastName,
    email: formData.email,
    affiliationId: formData.affiliationId,
    phone: `${formData.phone.prefix} ${formData.phone.phone}`,
    regionCode: formData.regionCode,
  }
}

function getPhonePlaceholder(region: string) {
  switch (region) {
    case 'GB':
      return '07000 000 000'
    default:
      return '0400 000 000'
  }
}

interface Props {
  geoRegion: string
}

function AgentHubExpressionOfInterestModal({
  geoRegion,
}: Props) {
  const dispatch = useAppDispatch()
  const intl = useIntl()

  const [regionCode, setRegionCode] = useState<App.AgentHubEnabledRegions>(geoRegion as App.AgentHubEnabledRegions)
  const [isError, setIsError] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [finished, setFinished] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [shouldDisplayAffiliationInput, setShouldDisplayAffiliationInput] = useState(false)
  const emailSendMutation = useMutation({
    mutationFn: async(userData: ExpressionOfInterestData) => {
      return requestPostExpressionOfInterest(userData)
    },
    onSuccess: async() => {
      setFinished(true)
      setIsError(false)
    },
    onError: async(error: App.ApiCallFailure) => {
      setErrorMsg(error.message)
      setIsError(true)
      setIsSubmitted(false)
    },
  })
  const fetching = useAppSelector((state) => state.agentHub.affiliationOptions.fetching)
  const affiliationOptions = useAppSelector((state) =>
    isAgentHubAffiliationRequired
      ? state.agentHub.affiliationOptions.options[regionCode]
      : [],
  )

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault()
    setIsSubmitted(true)
    const formData = formToObject<FormData>(e.currentTarget)
    const mappedFormData = mapFormData(formData)
    emailSendMutation.mutate(mappedFormData)
  }, [emailSendMutation])

  function handleAffiliationSelection(value: string) {
    if (value === 'Other (please specify)') {
      setShouldDisplayAffiliationInput(true)
      return
    }

    setShouldDisplayAffiliationInput(false)
  }

  useEffect(() => {
    if (isAgentHubAffiliationRequired) {
      dispatch(fetchAgentAffiliationOptions(regionCode))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [regionCode])

  const thankYouTitle = intl.formatMessage({
    id: 'GI0UW3UqUsxRWB6ypNPn',
    defaultMessage: 'Thank you!',
    description: 'Agent hub expression of interest modal title after successful registration',
  })
  const registerTitle = intl.formatMessage({
    id: 'qbRDiPubLnTRcjsNNoT7',
    defaultMessage: 'Register your interest',
    description: 'Agent hub expression of interest modal title for registration form',
  })
  const nameInvalidErrorMessage = intl.formatMessage({
    id: 'MPjVfmxwAQ0K0jPqIPKZ',
    defaultMessage: 'Invalid characters. Please use only letters and spaces',
    description: 'Validation error message for name fields with invalid characters in agent hub registration form',
  })

  return (<Modal size={finished ? 'S' : 'L'} title={finished ? thankYouTitle : registerTitle}>
    {isError && <MessageBanner kind="critical">
      {intl.formatMessage({
        id: 'ihYadzhJgw93Bn-FHUqB',
        defaultMessage: 'Something went wrong while setting up your registration. Error message: {error}',
        description: 'Error banner message shown when agent hub registration fails',
      }, { error: errorMsg })}
    </MessageBanner>}
    {finished && <BodyText variant="medium">
      {intl.formatMessage({
        id: 'Bf2ObLNlL9eglAEePq0c',
        defaultMessage: 'You have officially registered to join {name}. We will keep you notified with launch updates and next steps to verify your account.',
        description: 'Success message after completing agent hub expression of interest registration',
      }, { name: agentHubOrSubBrandConfig.displayShortName })}
    </BodyText>}
    {!finished && (<Group direction="vertical" gap={20}>
      <BodyText variant="medium">
        {intl.formatMessage({
          id: 'BGKVQS_WNydp50MFkVUf',
          defaultMessage: 'Join {name} to earn and unlock exclusive travel deals',
          description: 'Introductory body text on agent hub expression of interest registration form',
        }, { name: agentHubOrSubBrandConfig.displayShortName })}
      </BodyText>

      <form
        onSubmit={handleSubmit}
        name="agentHubExpressionOfInterestForm"
      >
        <InputGrid className={clsx({ showSpecifyAffiliation: shouldDisplayAffiliationInput })}>
          <TextInput
            className="grid-agencyName"
            label={intl.formatMessage({
              id: 'JV42CqtTy8BMtK7nxYLO',
              defaultMessage: 'Agency name',
              description: 'Label for agency name input field in agent hub registration form',
            })}
            maxLength={100}
            minLength={2}
            name="agencyName"
            placeholder={intl.formatMessage({
              id: 'P7NE26P18dXDoRV-mh2h',
              defaultMessage: 'Agency name',
              description: 'Placeholder for agency name input field in agent hub registration form',
            })}
            required
            requiredErrorMessage={intl.formatMessage({
              id: 'm6kVBRCBE7PMzX4g6aYT',
              defaultMessage: 'Agency name is required',
              description: 'Required validation error for agency name field in agent hub registration form',
            })}
          />
          <Select
            className="grid-regionCode"
            label={intl.formatMessage({
              id: 'dvnWIWrG-yYZhHZsOr7o',
              defaultMessage: 'Region',
              description: 'Label for region select field in agent hub registration form',
            })}
            name="regionCode"
            placeholder={intl.formatMessage({
              id: 'iTS9NnxcJgD6i4tm6_ZX',
              defaultMessage: 'Region',
              description: 'Placeholder for region select field in agent hub registration form',
            })}
            required
            requiredErrorMessage={intl.formatMessage({
              id: 'DbPjsRQd9r_0DQRUZul0',
              defaultMessage: 'Region is required',
              description: 'Required validation error for region field in agent hub registration form',
            })}
            value={regionCode}
            onChange={(e) => setRegionCode(e.target.value as App.AgentHubEnabledRegions)}
          >
            {config.regions.map((item) => item.name && <option key={item.code} value={item.code}>{item.name}</option>)}
          </Select>
          {isAgentHubAffiliationRequired && (
            <div className="grid-affiliation">
              <AgentHubAffiliationAutocomplete
                affiliationOptions={affiliationOptions}
                loading={fetching}
                onSelect={(value) => handleAffiliationSelection(value)}
              />
            </div>
          )}
          {shouldDisplayAffiliationInput &&
            <TextInput
              className="grid-specifyAffiliation"
              invalidErrorMessage={nameInvalidErrorMessage}
              label={intl.formatMessage({
                id: 's-yEHWHaeny9Qr4RwB-7',
                defaultMessage: 'Please specify affiliation',
                description: 'Label for specify affiliation input field in agent hub registration form',
              })}
              maxLength={100}
              minLength={2}
              name="specifyAffiliation"
              pattern="^[A-Za-z ]+$"
              placeholder={intl.formatMessage({
                id: 'MOq0g9oOUnemlARjUcMt',
                defaultMessage: 'Affiliation',
                description: 'Placeholder for specify affiliation input field in agent hub registration form',
              })}
              required={Boolean(shouldDisplayAffiliationInput)}
              requiredErrorMessage={intl.formatMessage({
                id: 'Xv8penG_nz0jpoueGrJx',
                defaultMessage: 'Affiliation is required',
                description: 'Required validation error for affiliation field in agent hub registration form',
              })}
            />}

          <TextInput
            className="grid-firstName"
            invalidErrorMessage={nameInvalidErrorMessage}
            label={intl.formatMessage({
              id: '8kM8-MYASivjkqLSLY8V',
              defaultMessage: 'First name',
              description: 'Label for first name input field in agent hub registration form',
            })}
            maxLength={100}
            minLength={2}
            name="firstName"
            pattern="^[A-Za-z ]+$"
            placeholder={intl.formatMessage({
              id: 'peVYSkwLaAeIiE_MJDh4',
              defaultMessage: 'First name',
              description: 'Placeholder for first name input field in agent hub registration form',
            })}
            required
            requiredErrorMessage={intl.formatMessage({
              id: 'Uh5nSfO8ANx4-9Rroa-p',
              defaultMessage: 'First name is required',
              description: 'Required validation error for first name field in agent hub registration form',
            })}
          />

          <TextInput
            className="grid-lastName"
            invalidErrorMessage={nameInvalidErrorMessage}
            label={intl.formatMessage({
              id: 'V--2oZtuQA6qU6J3qRwl',
              defaultMessage: 'Last name',
              description: 'Label for last name input field in agent hub registration form',
            })}
            maxLength={100}
            minLength={2}
            name="lastName"
            pattern="^[A-Za-z ]+$"
            placeholder={intl.formatMessage({
              id: 'rhLLwFARzDoGkD02mfMz',
              defaultMessage: 'Last name',
              description: 'Placeholder for last name input field in agent hub registration form',
            })}
            required
            requiredErrorMessage={intl.formatMessage({
              id: 'BBnSeC-4_A3wWe9pT0VX',
              defaultMessage: 'Last name is required',
              description: 'Required validation error for last name field in agent hub registration form',
            })}
          />

          <PhoneInput
            allowLandline
            className="grid-phone"
            prefixCountry={regionCode}
            defaultPhonePrefix={AGENT_HUB_DEFAULT_PHONE_NUMBER_PREFIX[regionCode]}
            label={intl.formatMessage({
              id: 'tu7pFTZeLt59IjqOMF0b',
              defaultMessage: 'Phone number',
              description: 'Label for phone number input field in agent hub registration form',
            })}
            name="phone"
            key={regionCode}
            placeholder={getPhonePlaceholder(regionCode)}
            required
            requiredErrorMessage={intl.formatMessage({
              id: 'ykqPpjxvRbZMwiRj9dMk',
              defaultMessage: 'Phone number is required',
              description: 'Required validation error for phone number field in agent hub registration form',
            })}
          />

          <TextInput
            className="grid-email"
            invalidErrorMessage={intl.formatMessage({
              id: 'fBQ1bwVmgLwIst8DngYa',
              defaultMessage: 'Invalid email address',
              description: 'Validation error for invalid email format in agent hub registration form',
            })}
            label={intl.formatMessage({
              id: '44FrARIAtpMgz-04hFkp',
              defaultMessage: 'Email address',
              description: 'Label for email address input field in agent hub registration form',
            })}
            maxLength={100}
            name="email"
            placeholder={intl.formatMessage({
              id: 'XhliSXVZRw7s1uDgJoES',
              defaultMessage: 'Enter your agency email address',
              description: 'Placeholder for email address input field in agent hub registration form',
            })}
            required
            requiredErrorMessage={intl.formatMessage({
              id: '8iiJVCJAhuwbBL7JzP_H',
              defaultMessage: 'Email address is required',
              description: 'Required validation error for email address field in agent hub registration form',
            })}
            type="email"
          />
        </InputGrid>
        <Group tabletGap="0 32" gap="16 0" tabletDirection="horizontal" direction="vertical" tabletHorizontalAlign="space-evenly" horizontalAlign="stretch">
          <BodyText variant="medium">
            <FormattedMessage
              id="hPQfE4MRqU4ypQhEX-y8"
              defaultMessage={
                'By continuing, you agree to our <termsLink>Terms & Conditions</termsLink> and ' +
                '<privacyLink>Privacy Policy</privacyLink>, and to subscribe to marketing communications for offers, alerts and services.'
              }
              description="Legal consent text with links to terms and privacy policy on agent hub registration form"
              values={{
                termsLink: (chunks) => <TextLink href={agentHubOrSubBrandConfig.termsUrl} rel="noopener noreferrer" target="_blank" weight="regular">{chunks}</TextLink>,
                privacyLink: (chunks) => <TextLink href={agentHubOrSubBrandConfig.policyUrl} rel="noopener noreferrer" target="_blank" weight="regular">{chunks}</TextLink>,
              }}
            />
          </BodyText>
          <TextButton disabled={isSubmitted} fit="mobile-full-width" kind="primary" size="large" type="submit">
            {intl.formatMessage({
              id: 'U8J6VtIiO9YXsx0wAERx',
              defaultMessage: 'Submit',
              description: 'Submit button text for agent hub expression of interest registration form',
            })}
          </TextButton>
        </Group>
      </form>
    </Group>)}
  </Modal>)
}

const mapStateToProps = (state: App.State) => ({
  geoRegion: state.geo.currentRegionCode,
})

export default connect(mapStateToProps)(AgentHubExpressionOfInterestModal)
